import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    ViewChild
} from "@angular/core";
import {Subscription} from "rxjs";
import {Color} from "../../ui-elements/color";
import {SessionEvents} from "../../orchestration/session-events";
import {TyroPayRequestError} from "../../payment/tyro";
import {
    PaymentModel
} from "../../payment/payment.model";
import {SessionModel} from "../../orchestration/session.model";
import {
    IntegratedPaymentProvider,
    PaymentAuthorisationType, WalletPaymentErrorType,
    WalletPaymentSubmissionFailure, WalletPaymentSubmissionResult
} from "../../payment/payment";
import {PaymentEvents} from "../../payment/payment-events";

@Component({
    selector: "woa-google-pay-button",
    templateUrl: "./google-pay-button.component.html"
})
export class GooglePayButtonComponent implements OnDestroy, OnChanges, AfterViewInit {
    @Input() passive: boolean = false;
    @Input() type: "plain" | "checkout";
    @Input() @HostBinding("class.disabled") disabled: boolean = false;
    @Input() showSpinner: boolean = false;

    @Output() pay: EventEmitter<void> = new EventEmitter<void>();
    @Output() paymentStarted: EventEmitter<void> = new EventEmitter<void>();
    @Output() paymentComplete: EventEmitter<WalletPaymentSubmissionResult> = new EventEmitter<WalletPaymentSubmissionResult>();
    @Output() paymentFailed: EventEmitter<WalletPaymentSubmissionFailure> = new EventEmitter<WalletPaymentSubmissionFailure>();
    @Output() paymentCancelled: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild("googlePayButtonContainer") protected googlePayButtonContainer: ElementRef;

    IntegratedPaymentProvider = IntegratedPaymentProvider;

    public color: "black" | "white";

    private subscriptions: Subscription = new Subscription();
    private paymentsClient: any;
    private tyro: any;
    private payForm: any;
    private googleButton: HTMLElement;
    private tyroPayFormHolder: Element;
    private destroyed: boolean = false;
    private paymentPlatformPublicKey: string;
    private payFormReady: boolean = false;
    private payFormError: string;
    private previousColor: "black" | "white";
    private previousType: "plain" | "checkout";

    constructor(private element: ElementRef,
                public paymentModel: PaymentModel,
                private changeDetectorRef: ChangeDetectorRef,
                private sessionModel: SessionModel) {
    }

    @HostBinding("class.woa-button") button = true;

    ngAfterViewInit(): void {
        this.subscriptions.add(PaymentEvents.paymentDetailsApplied$.subscribe(async () => {
            this.changeDetectorRef.detectChanges();
            return this.setupTyroPayForm();
        }));

        this.subscriptions.add(SessionEvents.currentApp$.subscribe(async () => {
            this.createGooglePayButton();
            return this.setupTyroPayForm();
        }));
    }

    async ngOnChanges(): Promise<void> {
        this.createGooglePayButton();
        return this.setupTyroPayForm();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
    }

    get isPassive(): boolean {
        return this.passive || this.paymentModel.paymentProvider !== IntegratedPaymentProvider.TYRO;
    }

    get showTyroSpinner(): boolean {
        return !this.isPassive && (!this.paymentModel.paymentPlatformPublicKey || (!this.payFormReady && !this.payFormError));
    }

    // noinspection JSUnusedGlobalSymbols
    createGooglePayButton(): void {
        if (!this.isPassive || !this.googlePayButtonContainer || this.destroyed) {
            return;
        }

        if (!this.paymentsClient) {
            this.paymentsClient = new (<any>window).google.payments.api.PaymentsClient({environment: "TEST"});
        }

        this.determineColor();
        if (this.previousColor === this.color && this.previousType === this.type) {
            return;
        }
        this.previousType = this.type;
        this.previousColor = this.color;

        if (this.googleButton) {
            this.googlePayButtonContainer.nativeElement.removeChild(this.googleButton);
        }
        // noinspection JSUnusedGlobalSymbols
        this.googleButton = this.paymentsClient.createButton({
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                if (!this.disabled) {
                    this.pay.emit();
                }
            },
            buttonColor: this.color,
            buttonType: this.type,
            buttonSizeMode: "fill"
        });
        this.googlePayButtonContainer.nativeElement.appendChild(this.googleButton);
    }

    async setupTyroPayForm(): Promise<void> {
        if (this.isPassive || this.paymentModel.paymentProvider !== IntegratedPaymentProvider.TYRO || this.destroyed) {
            return;
        }

        this.enableOrDisableTyroPayForm();

        // Tyro uses the following test to check that it can inject its form - we can't use a ViewChild, because it needs to be in the HTML DOM.
        this.tyroPayFormHolder = document.querySelector("#tyro-google-pay-form-holder");
        if (!this.tyroPayFormHolder) {
            setTimeout(async () => await this.setupTyroPayForm(), 500);
            return;
        }

        // The tyro secret (paymentPlatformPublicKey) changes if the order is re-initiated - the tyro form needs to be rebuilt.
        this.determineColor();
        if (!this.paymentModel.paymentPlatformPublicKey || this.paymentPlatformPublicKey === this.paymentModel.paymentPlatformPublicKey || (this.previousColor === this.color && this.previousType === this.type)) {
            return;
        }
        this.paymentPlatformPublicKey = this.paymentModel.paymentPlatformPublicKey;
        this.previousType = this.type;
        this.previousColor = this.color;

        try {
            console.log("Initialising Tyro for google pay button. Live Mode = ", this.paymentModel.paymentPlatformLiveMode);
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode
            });
            await this.tyro.init(this.paymentPlatformPublicKey);

            this.payForm = this.tyro.createPayForm({
                options: {
                    googlePay: {
                        enabled: true,
                        merchantInfo: {
                            merchantName: this.sessionModel.currentApp.googlePayMerchantName,
                            merchantId: this.paymentModel.paymentPlatformLiveMode ? this.sessionModel.currentApp.googlePayMerchantId : undefined
                        }
                    },
                    creditCardForm: {
                        enabled: false
                    },
                    walletButtons: {
                        singleButtonOnly: true
                    }
                },
                styleProps: {
                    googlePayButton: {
                        buttonColor: this.color,
                        buttonType: this.type
                    },
                    bodyMinWidth: 290,
                    bodyMaxWidth: 980,
                    labelPosition: "block",
                    walletPaymentsButtonsMargin: "0",
                    walletPaymentsButtonsHeight: "100%",
                    walletPaymentsButtonsWidth: "100%"
                }
            });
            if (!this.payForm.isGooglePaySupported()) {
                console.log("Tyro pay form does not support google pay");
                this.payFormError = "Tyro pay form does not support google pay";
                this.changeDetectorRef.detectChanges();
                return;
            }
            if ((<HTMLElement>this.tyroPayFormHolder).firstChild) {
                this.tyroPayFormHolder.removeChild((<HTMLElement>this.tyroPayFormHolder).firstChild);
            }
            await this.payForm.inject("#tyro-google-pay-form-holder");
            this.payForm.setReadyListener(() => {
                this.enableOrDisableTyroPayForm();
                this.payFormReady = true;
                this.changeDetectorRef.detectChanges();
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentBeginListener((paymentType: string) => {
                if (this.disabled) {
                    throw "Google pay button disabled";
                }
                this.paymentStarted.emit();
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentCompleteListener(async (paymentType: string, error: TyroPayRequestError) => {
                if (error) {
                    this.paymentFailed.emit(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.type, error.errorMessage, error.errorCode, error.gatewayCode));
                } else {
                    this.paymentComplete.emit(new WalletPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT));
                }
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentCancelledListener((paymentType: string) => this.paymentCancelled.emit());
        } catch (error) {
            console.log("Failed to create Tyro pay form for Google Pay button", error);
            this.payFormError = error.message.replace(/\.$/, "");
            this.changeDetectorRef.detectChanges();
        }
    }

    private determineColor(): void {
        const bg: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--theme-color-pair-background").trim();
        if (bg) {
            const rgb: string[] = bg.split(" ");
            const bgColor: Color = Color.fromRGB(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2]));
            this.color = bgColor.getContrastRatio(Color.BLACK) > bgColor.getContrastRatio(Color.WHITE) ? "black" : "white";
        }
    }

    private enableOrDisableTyroPayForm(): void {
        if (this.tyroPayFormHolder && (<HTMLElement>this.tyroPayFormHolder).firstChild) {
            (<HTMLElement>(<HTMLElement>this.tyroPayFormHolder).firstChild).tabIndex = this.disabled ? -1 : undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/buttons/google-pay-button/google-pay-button.component.ts